/**
 * MobX store for the admin "Activate Global-e Store" card: portal selection,
 * activation request via AdminMigrateGlobalEMerchantsService, and success/error UI state.
 */
import { makeAutoObservable, runInAction } from 'mobx';
import AdminMigrateGlobalEMerchantsService from '../../../../services/api/admin-migrate-global-e-merchants-service';
import { rootContainer, TYPE } from '../../../../services/container/root';
import { IShop } from '../../../../models/User';
import { isSet } from '../../../../models/model-types-methods';

class MerchantActivationStore {
    private _selectedShop?: IShop | null = null;
    private _isProcessing = false;
    private _showSuccess = false;
    private _errorMessage: string | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get service(): AdminMigrateGlobalEMerchantsService {
        return rootContainer.get<AdminMigrateGlobalEMerchantsService>(TYPE.AdminMigrateGlobalEMerchantsService);
    }

    public get portal(): IShop | null | undefined {
        return this._selectedShop;
    }

    public get isProcessing(): boolean {
        return this._isProcessing;
    }

    public get showSuccess(): boolean {
        return this._showSuccess;
    }

    public get errorMessage(): string | null {
        return this._errorMessage;
    }

    /** Portal selected in the shops' selector. */
    private get hasSelectedPortal(): boolean {
        return isSet(this._selectedShop);
    }

    /**
     * Custom return portal URL is enabled and non-empty (required for Global-e activation).
     */
    private get hasPortalUrlSetAndEnabled(): boolean {
        if (!isSet(this._selectedShop)) {
            return false;
        }

        const shop = this._selectedShop;
        if (!isSet(shop.use_custom_return_portal_url)) {
            return false;
        }

        const url = (shop.custom_return_portal_url ?? '').trim();
        return url.length > 0;
    }

    public get portalUrlFieldValue(): string {
        if (!isSet(this._selectedShop)) {
            return '';
        }

        const shop = this._selectedShop;
        if (!isSet(shop.use_custom_return_portal_url)) {
            return 'Custom portal URL is not activated';
        }
        return shop.custom_return_portal_url ?? '';
    }

    public get canSubmitActivation(): boolean {
        return this.hasSelectedPortal && this.hasPortalUrlSetAndEnabled;
    }

    public setPortal(shop: IShop|null|undefined): void {
        this._selectedShop = shop;
        this._showSuccess = false;
        this._errorMessage = null;
    }

    public get merchantIdDisplay(): string {
        return this._formatGlobaleField(this._selectedShop?.global_e_user_id);
    }

    public get globalEEnvDisplay(): string {
        return this._formatGlobaleField(this._selectedShop?.global_e_env);
    }

    private _formatGlobaleField(value: string | null | undefined): string {
        if (value === null || value === undefined || value === '') {
            return 'N/A';
        }
        return String(value);
    }

    public async activate(): Promise<void> {
        if (!this.canSubmitActivation || this._isProcessing) {
            return;
        }

        this._isProcessing = true;
        this._showSuccess = false;
        this._errorMessage = null;

        try {
            const result = await this.service.activateGlobaleMerchant({ portal_id: this._selectedShop!.id });
            runInAction(() => {
                if (result.successful_activation) {
                    this._showSuccess = true;
                } else {
                    this._errorMessage = 'Activation did not complete successfully.';
                }
            });
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'Unknown error during activation.';
            runInAction(() => {
                this._errorMessage = message;
            });
        } finally {
            runInAction(() => {
                this._isProcessing = false;
            });
        }
    }
}

export default new MerchantActivationStore();
